import type { IShellReplyValues, IShellSessionTokens } from '@featit/shared';
import { requestShell } from './shellBridge';
import { hasShellCapability } from './shellDetection';

/**
 * The app's sign-in, kept in the iOS Keychain (T-0320, ADR-115 amended).
 *
 * The second implementation of `desktop/desktopSessionVault.ts`'s seam, and it works the same way:
 * `localStorage` carries the session while the page runs, and the vault carries it across launches.
 * A web view's `localStorage` is wiped with its website data, and a token is a secret, so it belongs
 * in the Keychain anyway.
 *
 * ⛔ **No address is sent.** The shell files the session under the deployment of the web view the
 * message came from. ⛔ **Only the web refreshes.** The shell stores what it is given and never calls
 * the API with it.
 *
 * A leaf, like the desktop vault: `ApiService` is one of its callers.
 */

/**
 * Run a vault request, or give up quietly. None of these may fail what it accompanies: a Keychain
 * that cannot be reached must not stop somebody signing in, and a read that does not answer must
 * not stop the app starting — that costs one sign-in, which is what happened before the vault.
 */
async function attempt<TResult>(
  description: string,
  run: () => Promise<TResult>,
  fallback: TResult,
): Promise<TResult> {
  if (!hasShellCapability('sessionVault')) return fallback;
  try {
    return await run();
  } catch (error) {
    console.warn(`[featit] shell session vault: ${description} failed`, error);
    return fallback;
  }
}

export function readShellSession(): Promise<IShellSessionTokens | null> {
  return attempt(
    'read',
    async () => {
      const stored = await requestShell<IShellReplyValues['session.read']>({
        type: 'session',
        action: 'read',
      });
      return stored && typeof stored.accessToken === 'string' ? stored : null;
    },
    null,
  );
}

/**
 * T-0666 (ADR-100's T-0666 amendment, 5): this page's writes reach the Keychain in the order they
 * were made, so a fire-and-forget mirror never lands after a newer chain write.
 */
let writes: Promise<unknown> = Promise.resolve();

function queuedWrite<TResult>(run: () => Promise<TResult>): Promise<TResult> {
  const next = writes.then(run, run);
  writes = next.catch(() => undefined);
  return next;
}

async function sendSessionWrite(tokens: IShellSessionTokens): Promise<void> {
  await requestShell<IShellReplyValues['session.write']>({
    type: 'session',
    action: 'write',
    tokens,
  });
}

export function writeShellSession(tokens: IShellSessionTokens): Promise<void> {
  return queuedWrite(() => attempt('write', () => sendSessionWrite(tokens), undefined));
}

/**
 * T-0666: the write-ahead chain, written before the request that names its newest candidate. Says
 * whether the Keychain kept it — `false` for a refusal (the shell answers a failed store with an
 * error) — so the caller names nothing the vault does not hold. `true` outside the shell.
 */
export function writeShellSessionDurably(tokens: IShellSessionTokens): Promise<boolean> {
  if (!hasShellCapability('sessionVault')) return Promise.resolve(true);
  return queuedWrite(() =>
    attempt('chain write', () => sendSessionWrite(tokens).then(() => true), false),
  );
}

export function clearShellSession(): Promise<void> {
  return attempt(
    'clear',
    async () => {
      await requestShell<IShellReplyValues['session.clear']>({ type: 'session', action: 'clear' });
    },
    undefined,
  );
}
